import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Rating
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReviewReplySection from './ReviewReplySection';
import { apiService } from '../../utils/api';
import { normalizeImageUrl } from '../Post';

/**
 * ReviewTimeline - クチコミ一覧のタイムラインビュー
 */
const ReviewTimeline = ({
  reviews,
  currentUserId,
  isOwner,
  isTaggedCorp,
  onApproved,
  onUpdated,
  onDeleted,
  onRefresh
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editRating, setEditRating] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleMenuOpen = (e, reviewId) => {
    setAnchorEl(e.currentTarget);
    setSelectedReviewId(reviewId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReviewId(null);
  };

  const handleEditClick = (review) => {
    setEditingReviewId(review.id);
    setEditText(review.text);
    setEditRating(review.rating || 3);
  };

  const handleEditCancel = () => {
    setEditingReviewId(null);
    setEditText('');
    setEditRating(3);
    setError('');
  };

  const handleEditSave = async () => {
    if (editText.length < 5) {
      setError('クチコミは5文字以上で入力してください');
      return;
    }

    try {
      setLoading(true);
      await apiService.put(`/api/reviews/${editingReviewId}`, {
        text: editText.trim(),
        rating: editRating
      });
      setEditingReviewId(null);
      setEditText('');
      setEditRating(3);
      setError('');
      handleMenuClose();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || 'クチコミの更新に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      setLoading(true);
      await apiService.put(`/api/reviews/${reviewId}/approve`);
      handleMenuClose();
      if (onApproved) onApproved();
    } catch (err) {
      setError(err.response?.data?.error || 'クチコミの承認に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (window.confirm('このクチコミを削除しますか？')) {
      try {
        setLoading(true);
        await apiService.delete(`/api/reviews/${reviewId}`);
        handleMenuClose();
        if (onDeleted) onDeleted();
      } catch (err) {
        setError(err.response?.data?.error || 'クチコミの削除に失敗しました');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {reviews.map((review) => (
        <Card key={review.id} sx={{ mb: 2 }}>
          <CardContent>
            {/* クチコミヘッダー */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                <Avatar
                  src={normalizeImageUrl(review.reviewer_avatar)}
                  sx={{ width: 40, height: 40 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {review.reviewer_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Rating
                      value={review.rating || 3}
                      readOnly
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      ({review.rating || 3}/5)
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {formatDate(review.created_at)}
                  </Typography>
                  {!review.approved && (
                    <Chip
                      label={review.is_own ? '承認待ち（公開前）' : '承認待ち'}
                      color="warning"
                      size="small"
                      sx={{ ml: 0, mt: 0.5 }}
                    />
                  )}
                  {!review.approved && review.is_own && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      このクチコミは承認されると公開されます。承認までは投稿者本人にのみ表示されます。
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* メニューボタン */}
              {(review.is_deletable || review.is_editable || review.is_approvable) && (
                <>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, review.id)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={selectedReviewId === review.id && !!anchorEl}
                    onClose={handleMenuClose}
                  >
                    {review.is_editable && (
                      <MenuItem onClick={() => handleEditClick(review)}>
                        <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                        編集
                      </MenuItem>
                    )}
                    {review.is_approvable && (
                      <MenuItem onClick={() => handleApprove(review.id)}>
                        <CheckCircleIcon sx={{ mr: 1, fontSize: 18, color: 'success.main' }} />
                        承認
                      </MenuItem>
                    )}
                    {review.is_deletable && (
                      <MenuItem onClick={() => handleDelete(review.id)} sx={{ color: 'error.main' }}>
                        <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                        削除
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>

            {/* クチコミテキスト */}
            {editingReviewId === review.id ? (
              <Box sx={{ mt: 2 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                  </Alert>
                )}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption">評価:</Typography>
                  <Rating
                    value={editRating}
                    onChange={(e, value) => setEditRating(value || 3)}
                    disabled={loading}
                  />
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  disabled={loading}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleEditSave}
                    disabled={loading}
                  >
                    保存
                  </Button>
                  <Button
                    size="small"
                    onClick={handleEditCancel}
                    disabled={loading}
                  >
                    キャンセル
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 1, mb: 2, whiteSpace: 'pre-wrap' }}>
                {review.text}
              </Typography>
            )}
          </CardContent>

          {/* 返信セクション */}
          {editingReviewId !== review.id && (
            <>
              <Divider />
              <ReviewReplySection
                reviewId={review.id}
                currentUserId={currentUserId}
                isOwner={isOwner}
                isTaggedCorp={isTaggedCorp}
                onRefresh={onRefresh}
              />
            </>
          )}
        </Card>
      ))}
    </Box>
  );
};

export default ReviewTimeline;
